import { applyDraftLineEdits, numberDraftLines } from "../ai/storyEngine/write/draftLineEdits";
import { normalizeQuoteDirection } from "../lib/normalizeQuoteDirection";

/** Group nearby replacements into readable phrases, retaining exact model reasons. */
export function buildDraftEditSegments(before, after, edits) {
  // Historical records must replay their literal edits, even if live validation
  // would now reject the punctuation. Keep all other checks and stored-after equality.
  const expected = normalizeQuoteDirection(applyDraftLineEdits(before, edits, { validatePunctuation: false }));
  if (expected !== after) throw new Error("Stored edit result does not match validated replacements");
  let offset = 0;
  const lines = numberDraftLines(before).map(line => {
    const item = { ...line, offset };
    offset += line.text.length + line.ending.length;
    return item;
  });
  const ranges = edits.map(edit => {
    const line = lines[edit.lineNumber - 1];
    const start = line.offset + line.text.indexOf(edit.textToReplace);
    return { start, end: start + edit.textToReplace.length, replacement: edit.replacementText, reason: edit.reason };
  }).sort((a, b) => a.start - b.start);
  const groups = [];
  for (const range of ranges) {
    const prior = groups[groups.length - 1];
    const gap = prior ? before.slice(prior.end, range.start) : "";
    // Never merge across a line/paragraph. A short untouched join belongs to the phrase.
    if (prior && !/[\r\n]/.test(gap) && gap.length <= 60 && gap.trim().split(/\s+/).filter(Boolean).length <= 8) {
      prior.end = range.end;
      prior.edits.push(range);
    } else groups.push({ start: range.start, end: range.end, edits: [range] });
  }
  const segments = [];
  let originalOffset = 0, editedOffset = 0;
  const add = (original, replacement, reasons, changed) => {
    if (!original && !replacement) return;
    // Quote normalization preserves character offsets. Slice canonical delivered text,
    // rather than independently normalizing fragments at artificial boundaries.
    const canonical = after.slice(editedOffset, editedOffset + replacement.length);
    segments.push({ before: original, after: canonical, reasons, changed });
    editedOffset += replacement.length;
  };
  for (const group of groups) {
    const unchanged = before.slice(originalOffset, group.start);
    add(unchanged, unchanged, [], false);
    const original = before.slice(group.start, group.end);
    let replacement = original;
    for (const edit of [...group.edits].reverse()) {
      replacement = replacement.slice(0, edit.start - group.start) + edit.replacement + replacement.slice(edit.end - group.start);
    }
    add(original, replacement, [...new Set(group.edits.map(e => e.reason).filter(Boolean))], original !== replacement);
    originalOffset = group.end;
  }
  const tail = before.slice(originalOffset);
  add(tail, tail, [], false);
  if (editedOffset !== after.length) throw new Error("Diff offsets did not preserve delivered prose");
  return segments;
}

export function getDraftEditRecord(completion) {
  if (completion?.functionName !== "draft/editPass") return null;
  const stored = completion.humanloop_metadata?.draftEditPass;
  if (stored?.status !== "validated") return { error: stored?.error || "The validated edit result has not been saved yet. Refresh this invocation after the draft finishes." };
  try {
    return { ...stored, segments: buildDraftEditSegments(stored.before, stored.after, stored.edits) };
  } catch (error) { return { error: error.message }; }
}
